import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ShieldCheck, ExternalLink, Loader2, ArrowLeft, ChevronRight, Sparkles,
} from 'lucide-react';
import PublicShell from '../components/PublicShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────── Index page (lists all countries with provenance data) ─────────

export function ProvenanceIndexPage({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/public/provenance`)
      .then(res => setData(res.data))
      .catch(() => setData({ countries: [], total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicShell user={user}>
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl lg:ml-0 lg:mr-auto">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold uppercase tracking-widest mb-4">
            <ShieldCheck className="w-3 h-3" /> Public · Zero-Hallucination Audit Trail
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0b1c30]">Data Provenance</h1>
          <p className="mt-3 text-base text-[#434655] max-w-3xl">
            Every number our AI generated for every country, with the original source URL and the fetched-at timestamp. Click any country to see exactly which authoritative datapoints anchored its analysis. Nothing is fabricated, every figure can be independently re-fetched.
          </p>
        </div>

        {/* Top-line stats */}
        {!loading && data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Countries audited',  value: data.total },
              { label: 'Observations on file', value: data.countries.reduce((a, c) => a + c.observations, 0) },
              { label: 'Distinct providers',  value: new Set(data.countries.flatMap(c => c.providers)).size },
              { label: 'High-confidence rows', value: data.countries.reduce((a, c) => a + c.high_confidence, 0) },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#c3c6d7]/40 rounded-xl p-4 shadow-sm">
                <div className="text-[10px] uppercase tracking-widest text-[#737686] font-bold">{s.label}</div>
                <div className="text-2xl font-extrabold tracking-tighter text-[#0b1c30] mt-1">{s.value.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#737686]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading audit catalog…
          </div>
        )}

        {!loading && data && data.countries.length === 0 && (
          <div className="bg-white border border-[#c3c6d7]/40 rounded-xl p-8 text-center text-sm text-[#737686]">
            No public provenance data yet. As soon as a country blueprint is generated, its audit pack will appear here.
          </div>
        )}

        {!loading && data && data.countries.length > 0 && (
          <div className="bg-white border border-[#c3c6d7]/40 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-emerald-50 text-emerald-900 text-[10px] uppercase tracking-widest font-bold">
                <tr>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Sector</th>
                  <th className="text-right px-4 py-3">Observations</th>
                  <th className="text-right px-4 py-3">High-confidence</th>
                  <th className="text-left px-4 py-3">Providers</th>
                  <th className="text-left px-4 py-3">Last fetched</th>
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.countries.map((c) => (
                  <tr key={c.country_slug} className="border-t border-[#c3c6d7]/30 hover:bg-emerald-50/30">
                    <td className="px-4 py-3 font-bold text-[#0b1c30]">
                      <Link to={`/provenance/${c.country_slug}`} className="hover:text-[#004ac6]" data-testid={`prov-row-${c.country_slug}`}>
                        {c.country_name} <span className="text-[#737686] font-mono text-[11px] ml-1">{c.country_iso}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[#434655] text-xs">{c.sector || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.observations.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-xs">{c.high_confidence}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {c.providers.slice(0, 3).map(p => (
                          <span key={p} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#f3f4f6] text-[#434655]">{p}</span>
                        ))}
                        {c.providers.length > 3 && <span className="text-[10px] text-[#737686]">+{c.providers.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#737686] font-mono">{(c.last_fetched_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-3 text-right">
                      <Link to={`/provenance/${c.country_slug}`} className="text-[#004ac6] hover:underline inline-flex items-center gap-1 text-xs">
                        Open <ChevronRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-10 p-5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <div className="font-bold mb-1">Why this matters.</div>
              <p className="text-[#065f46] leading-relaxed">
                Tier-one consultancies don't publish their working data. We do — for every country and every figure — because the value is in the synthesis, not the secrecy. If you want to verify any of these numbers yourself, the source URL takes you straight to the original dataset.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

// ───────── Detail page (one country's full audit pack) ─────────

export function ProvenanceCountryPage({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/public/provenance/${slug}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Group by section for clean display.
  const grouped = {};
  (data?.observations || []).forEach((o) => {
    const sec = o.section || 'unknown';
    grouped[sec] = grouped[sec] || [];
    grouped[sec].push(o);
  });

  return (
    <PublicShell user={user}>
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl lg:ml-0 lg:mr-auto">
        <button onClick={() => navigate('/provenance')} className="text-xs text-[#004ac6] hover:underline inline-flex items-center gap-1 mb-4" data-testid="prov-back">
          <ArrowLeft className="w-3 h-3" /> Back to all countries
        </button>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#737686]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading audit pack…
          </div>
        )}

        {!loading && !data && (
          <div className="bg-white border border-[#c3c6d7]/40 rounded-xl p-8 text-center text-sm text-[#737686]">
            No provenance data found for this country.
          </div>
        )}

        {!loading && data && (
          <>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold uppercase tracking-widest mb-3">
                <ShieldCheck className="w-3 h-3" /> Audit Pack
              </div>
              <h1 className="text-4xl font-extrabold tracking-tighter text-[#0b1c30]">{data.country_name}</h1>
              <p className="mt-2 text-base text-[#434655]">
                {data.total} observations across {data.section_count} {data.section_count === 1 ? 'section' : 'sections'}, drawn from {data.providers.length} authoritative {data.providers.length === 1 ? 'source' : 'sources'}. Every figure links back to its original dataset.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {data.providers.map(p => (
                  <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white border border-[#c3c6d7]/40 text-[#434655]">{p}</span>
                ))}
              </div>
            </div>

            {Object.entries(grouped).map(([section, rows]) => (
              <div key={section} className="mb-8 bg-white border border-[#c3c6d7]/40 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 text-xs uppercase tracking-widest font-bold text-emerald-900">
                  {section.replace(/_/g, ' ')} <span className="ml-2 text-[10px] opacity-60 normal-case tracking-normal">{rows.length} observations</span>
                </div>
                <table className="w-full text-[11px]">
                  <thead className="bg-[#f8f9ff] text-[#737686] text-[10px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-2">Metric</th>
                      <th className="text-right px-2 py-2">Value</th>
                      <th className="text-left px-2 py-2">Year</th>
                      <th className="text-left px-2 py-2">Provider</th>
                      <th className="text-left px-2 py-2">Confidence</th>
                      <th className="text-left px-2 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((o, i) => {
                      const v = o.value;
                      const valStr = typeof v === 'number'
                        ? (Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2))
                        : String(v ?? '—');
                      const confColor = o.confidence === 'high'
                        ? 'bg-emerald-500/15 text-emerald-700'
                        : o.confidence === 'medium'
                          ? 'bg-amber-400/20 text-amber-800'
                          : 'bg-red-400/20 text-red-700';
                      return (
                        <tr key={i} className="border-t border-[#c3c6d7]/20 hover:bg-emerald-50/40">
                          <td className="px-4 py-1.5 text-[#0b1c30]">{o.metric_label}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-[#0b1c30]">
                            {valStr}{o.unit && o.unit !== 'status' ? <span className="opacity-60 ml-1">{o.unit}</span> : null}
                          </td>
                          <td className="px-2 py-1.5">{o.year || (o.as_of ? o.as_of.slice(0, 7) : '—')}</td>
                          <td className="px-2 py-1.5">{o.provider}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${confColor}`}>
                              {o.confidence || 'medium'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            {o.source_url
                              ? <a href={o.source_url} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 text-[#004ac6] hover:underline">
                                  Open <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              : <span className="opacity-40">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
      </div>
    </PublicShell>
  );
}
